// SCR-015's Credentials panel: shown while a session is `awaiting_credentials`. Pasted STS keys
// (mode `sts_keys`) are sent once and the draft is cleared from this component's own state before
// the request settles (REQ-044) — never logged, never kept past that submit. IAM Identity Center
// (mode `identity_center`) instead walks the device-authorization flow; the bootstrap role needs no
// form at all.
import { useState } from "preact/hooks";
import type { JSX } from "preact";
import { useApi } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { ErrorDetails } from "@nexus/ui";
import { Field, errorId } from "@nexus/ui";
import { Card, CopyId } from "@nexus/ui";
import { Icon } from "@nexus/ui";
import {
  clearCredentialsDraft,
  credentialsAreValid,
  credentialsRequestBody,
  emptyCredentialsDraft,
  emptyIdentityCenterStart,
  identityCenterSelectRequest,
  identityCenterStartRequest,
  sessionActionErrorMessage,
  validateCredentialsDraft,
  validateIdentityCenterStart,
  visibleText,
  type CredentialsDraft,
  type IdentityCenterPollResponse,
  type IdentityCenterStartResponse,
  type InstallerAccessMode,
} from "./model";

export interface CredentialsPanelProps {
  sessionId: string;
  accessMode: InstallerAccessMode;
  /** Called once the session has what it needs and can leave `awaiting_credentials`. */
  onSubmitted(): void;
}

export function CredentialsPanel({ sessionId, accessMode, onSubmitted }: CredentialsPanelProps) {
  if (accessMode === "bootstrap_role") {
    return (
      <Card title="Credentials" titleId="credentials-heading" help="installer.accessMode" class="credentials-panel">
        <p>This session uses the linked account's bootstrap role; there is nothing to enter here.</p>
      </Card>
    );
  }
  return accessMode === "sts_keys" ? (
    <StsCredentialsForm sessionId={sessionId} onSubmitted={onSubmitted} />
  ) : (
    <IdentityCenterFlow sessionId={sessionId} onSubmitted={onSubmitted} />
  );
}

function PanelError({ error }: { error: ApiError }) {
  return (
    <div role="alert" class="banner banner-error">
      <p>{sessionActionErrorMessage(error)}</p>
      <ErrorDetails error={error} />
    </div>
  );
}

function StsCredentialsForm({ sessionId, onSubmitted }: { sessionId: string; onSubmitted(): void }) {
  const api = useApi();
  const [draft, setDraft] = useState<CredentialsDraft>(emptyCredentialsDraft);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const errors = validateCredentialsDraft(draft);
  const showErrors = draft.accessKeyId !== "" || draft.secretAccessKey !== "" || draft.sessionToken !== "";
  const keyError = showErrors ? errors.accessKeyId : undefined;

  async function submit(event: JSX.TargetedEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!api || !credentialsAreValid(draft)) return;
    const body = credentialsRequestBody(draft);
    // Clear this panel's own state the instant the body is built — the credentials are never held
    // here past this submit (REQ-044, TASK-122's security requirement), win or lose.
    setDraft(clearCredentialsDraft());
    setBusy(true);
    setError(null);
    try {
      await api.post("/v1/installer/sessions/{sessionId}/credentials", { path: { sessionId }, body });
      onSubmitted();
    } catch (e) {
      if (!(e instanceof ApiError)) throw e;
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (!api) return <p role="alert">Admin API not available.</p>;

  return (
    <Card title="Credentials" titleId="credentials-heading" help="installer.accessMode" class="credentials-panel">
      <p class="muted">
        Paste the temporary (<code>ASIA…</code>) STS credentials for this account. They are held only until this form
        submits, never logged, and refused here if they are long-term (<code>AKIA…</code>) keys.
      </p>
      {error && <PanelError error={error} />}
      <form class="stack-sm" onSubmit={(event) => void submit(event)}>
        <Field id="cred-access-key" label="Access key id" help="installer.accessKeyId" error={keyError}>
          <input
            id="cred-access-key"
            name="accessKeyId"
            type="text"
            class="input-mono"
            autocomplete="off"
            spellcheck={false}
            value={draft.accessKeyId}
            aria-invalid={keyError ? "true" : undefined}
            aria-describedby={keyError ? errorId("cred-access-key") : undefined}
            onInput={(event) => setDraft({ ...draft, accessKeyId: event.currentTarget.value })}
          />
        </Field>
        <Field id="cred-secret" label="Secret access key" help="installer.secretAccessKey">
          <input
            id="cred-secret"
            name="secretAccessKey"
            type="password"
            autocomplete="off"
            value={draft.secretAccessKey}
            onInput={(event) => setDraft({ ...draft, secretAccessKey: event.currentTarget.value })}
          />
        </Field>
        <Field id="cred-token" label="Session token" help="installer.sessionToken">
          <textarea
            id="cred-token"
            name="sessionToken"
            class="input-mono"
            autocomplete="off"
            spellcheck={false}
            value={draft.sessionToken}
            onInput={(event) => setDraft({ ...draft, sessionToken: event.currentTarget.value })}
          />
        </Field>
        <div class="form-actions">
          <button type="submit" class="button button-primary" disabled={!credentialsAreValid(draft) || busy}>
            {busy ? "Sending…" : "Send credentials"}
          </button>
        </div>
      </form>
    </Card>
  );
}

function IdentityCenterFlow({ sessionId, onSubmitted }: { sessionId: string; onSubmitted(): void }) {
  const api = useApi();
  const [draft, setDraft] = useState(emptyIdentityCenterStart);
  const [start, setStart] = useState<IdentityCenterStartResponse | null>(null);
  const [poll, setPoll] = useState<IdentityCenterPollResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const errors = validateIdentityCenterStart(draft);
  const urlError = draft.startUrl !== "" ? errors.startUrl : undefined;
  const regionError = draft.region !== "" ? errors.region : undefined;

  async function beginAuthorization(event: JSX.TargetedEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!api || Object.keys(errors).length > 0) return;
    setBusy(true);
    setError(null);
    try {
      const response = await api.post("/v1/installer/sessions/{sessionId}/identity-center/start", {
        path: { sessionId },
        body: identityCenterStartRequest(draft),
      });
      setStart(response);
    } catch (e) {
      if (!(e instanceof ApiError)) throw e;
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function checkStatus() {
    if (!api) return;
    setError(null);
    try {
      setPoll(await api.post("/v1/installer/sessions/{sessionId}/identity-center/poll", { path: { sessionId } }));
    } catch (e) {
      if (!(e instanceof ApiError)) throw e;
      setError(e);
    }
  }

  async function selectRole(accountId: string, roleName: string) {
    if (!api) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/v1/installer/sessions/{sessionId}/identity-center/select", {
        path: { sessionId },
        body: identityCenterSelectRequest(accountId, roleName),
      });
      onSubmitted();
    } catch (e) {
      if (!(e instanceof ApiError)) throw e;
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (!api) return <p role="alert">Admin API not available.</p>;

  return (
    <Card title="IAM Identity Center" titleId="idc-heading" help="installer.accessMode" class="credentials-panel">
      {error && <PanelError error={error} />}
      {!start && (
        <form class="stack-sm" onSubmit={(event) => void beginAuthorization(event)}>
          <p class="muted">Sign in to the customer's IAM Identity Center with a one-time device code, then pick the role to use.</p>
          <Field id="idc-start-url" label="Start URL" help="installer.idcStartUrl" error={urlError}>
            <input
              id="idc-start-url"
              type="url"
              placeholder="https://example.awsapps.com/start"
              autocomplete="off"
              value={draft.startUrl}
              aria-invalid={urlError ? "true" : undefined}
              aria-describedby={urlError ? errorId("idc-start-url") : undefined}
              onInput={(event) => setDraft({ ...draft, startUrl: event.currentTarget.value })}
            />
          </Field>
          <Field id="idc-region" label="Region" help="installer.idcRegion" error={regionError} class="field-narrow">
            <input
              id="idc-region"
              type="text"
              placeholder="us-east-1"
              autocomplete="off"
              value={draft.region}
              aria-invalid={regionError ? "true" : undefined}
              aria-describedby={regionError ? errorId("idc-region") : undefined}
              onInput={(event) => setDraft({ ...draft, region: event.currentTarget.value })}
            />
          </Field>
          <div class="form-actions">
            <button type="submit" class="button button-primary" disabled={busy || Object.keys(errors).length > 0}>
              Start device authorization
            </button>
          </div>
        </form>
      )}
      {start && (
        <div class="idc-device stack-sm">
          <ol class="idc-steps">
            <li>
              Open the sign-in page:{" "}
              <a href={start.verificationUriComplete} target="_blank" rel="noreferrer">
                {start.verificationUriComplete}
                <Icon name="external" small />
              </a>
            </li>
            <li>
              Check that it shows this code: <CopyId id={start.userCode} label="" what="code" />
            </li>
            <li>Approve it, then come back here.</li>
          </ol>
          <div class="row">
            <button type="button" class="button button-secondary" onClick={() => void checkStatus()} disabled={busy}>
              I authorized it
            </button>
          </div>
          {poll?.status === "pending" && <p role="status">Waiting for authorization…</p>}
          {poll?.status === "expired" && (
            <p role="alert" class="banner banner-warning">
              The code expired; start again.
            </p>
          )}
          {poll?.status === "authorized" && poll.accounts && (
            <ul class="idc-accounts">
              {poll.accounts.map((account) => (
                <li key={account.accountId}>
                  <p>
                    {visibleText(account.accountName)} <span class="mono cell-sub">{account.accountId}</span>
                  </p>
                  <div class="row">
                    {account.roles.map((role) => (
                      <button
                        key={role}
                        type="button"
                        class="button button-sm"
                        disabled={busy}
                        onClick={() => void selectRole(account.accountId, role)}
                      >
                        Use {visibleText(role)}
                      </button>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}
